<template>
  <!-- Design-system look: the night view of Munich behind the sign-in pages, brought to life with
       the website footer's ideas (airlst2027 www/footer.php): the ferris wheel's lamps run round its
       rim, the free-fall tower's gondola rides up and drops, smoke drifts from the Oktoberfest
       tents, the Olympic Tower's light blinks, stars twinkle and a few bright ones glint, the
       Allianz Arena's glow breathes, the Isar shimmers, and fireworks go up over the Theresienwiese
       (nightSkyFireworks.js).
       Everything is placed in the picture's own coordinates (1672 x 941) and drawn with the same
       cover fit as the picture, so it stays on its spot at any window size. The picture's paint
       (lamp gold, beacon red) belongs to the artwork, like the picture itself. -->
  <div class="ds-night-sky" aria-hidden="true" data-test="auth-night-sky">
    <svg
      class="ds-night-sky__art"
      viewBox="0 0 1672 941"
      preserveAspectRatio="xMidYMid slice"
      focusable="false"
    >
      <defs>
        <radialGradient :id="id('bulb')" r=".5">
          <stop offset="0" stop-color="#fff4d8" />
          <stop offset=".4" stop-color="#ffd27a" stop-opacity=".85" />
          <stop offset="1" stop-color="#ffb347" stop-opacity="0" />
        </radialGradient>
        <radialGradient :id="id('beacon')" r=".5">
          <stop offset="0" stop-color="#ff6a58" stop-opacity=".9" />
          <stop offset=".5" stop-color="#ff3b30" stop-opacity=".25" />
          <stop offset="1" stop-color="#ff3b30" stop-opacity="0" />
        </radialGradient>
        <radialGradient :id="id('smoke')" r=".5">
          <stop offset="0" stop-color="#ffe3c4" stop-opacity=".5" />
          <stop offset=".45" stop-color="#e9e2dc" stop-opacity=".2" />
          <stop offset="1" stop-color="#d9dbe6" stop-opacity="0" />
        </radialGradient>
        <radialGradient :id="id('arena')" r=".5">
          <stop offset="0" stop-color="#ff5a3c" stop-opacity=".55" />
          <stop offset=".6" stop-color="#ff2d2d" stop-opacity=".18" />
          <stop offset="1" stop-color="#ff2d2d" stop-opacity="0" />
        </radialGradient>
        <filter
          :id="id('water')"
          filterUnits="userSpaceOnUse"
          x="380"
          y="848"
          width="870"
          height="93"
        >
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.012 0.09"
            numOctaves="2"
            seed="4"
          >
            <animate
              attributeName="baseFrequency"
              dur="14s"
              values="0.012 0.09; 0.014 0.11; 0.012 0.09"
              repeatCount="indefinite"
            />
          </feTurbulence>
          <feDisplacementMap
            in="SourceGraphic"
            scale="7"
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
        <clipPath :id="id('isar')">
          <path
            d="M385 862 L900 856 L1000 872 L1100 895 L1180 915 L1240 941 L430 941 L395 905 Z"
          />
        </clipPath>
      </defs>

      <image
        :href="picture"
        x="0"
        y="0"
        width="1672"
        height="941"
        preserveAspectRatio="none"
      />

      <!-- Isar: the painted water, gently rippled -->
      <g class="isar" :clip-path="`url(#${id('isar')})`">
        <image
          :href="picture"
          x="0"
          y="0"
          width="1672"
          height="941"
          preserveAspectRatio="none"
          :filter="`url(#${id('water')})`"
        />
      </g>

      <!-- Stars: many small ones twinkling each at its own pace, and a few bright ones that glint -->
      <g class="stars" fill="#fff6e6">
        <circle
          v-for="star in STARS"
          :key="`${star.x}-${star.y}`"
          :cx="star.x"
          :cy="star.y"
          :r="star.r"
          :style="star.style"
        />
      </g>
      <g class="glints" fill="#fff6e6">
        <g
          v-for="glint in GLINTS"
          :key="`${glint.x}-${glint.y}`"
          :transform="`translate(${glint.x} ${glint.y})`"
        >
          <g class="glint" :style="glint.style">
            <path
              d="M0 -8 L1.1 -1.1 L8 0 L1.1 1.1 L0 8 L-1.1 1.1 L-8 0 L-1.1 -1.1 Z"
            />
            <circle r="1.5" />
          </g>
        </g>
      </g>

      <!-- Olympic Tower: the obstruction light at the top of the antenna -->
      <g class="beacon" transform="translate(1495.5 529)">
        <circle r="9" :fill="`url(#${id('beacon')})`" />
        <circle r="1.6" fill="#ff5a4a" />
      </g>

      <!-- Allianz Arena: the stadium glow breathes -->
      <ellipse
        class="arena"
        cx="1600"
        cy="808"
        rx="110"
        ry="70"
        :fill="`url(#${id('arena')})`"
      />

      <!-- Oktoberfest: smoke and steam rise from the tents and drift away -->
      <g class="smoke">
        <g v-for="puff in PUFFS" :key="puff.x" class="puff" :style="puff.style">
          <ellipse rx="12" ry="8" :fill="`url(#${id('smoke')})`" />
        </g>
      </g>

      <!-- Free-fall tower (x 1316.7): the lit gondola ring rides up the column and drops -->
      <g class="freefall">
        <ellipse rx="7" ry="1.8" fill="#ffd27a" />
        <ellipse rx="11" ry="4" :fill="`url(#${id('bulb')})`" opacity=".9" />
      </g>

      <!-- Ferris wheel (centre 1179/775, rim r 32.5): lamps run round the rim -->
      <g transform="translate(1179 775)">
        <g class="wheel">
          <g
            v-for="angle in BULBS"
            :key="angle"
            :transform="`rotate(${angle})`"
          >
            <circle
              class="bulb"
              cy="-32.5"
              r="4"
              :fill="`url(#${id('bulb')})`"
            />
          </g>
        </g>
      </g>
    </svg>

    <canvas ref="fireworks" class="ds-night-sky__fireworks" />
  </div>
</template>

<script setup>
import { startFireworks } from '@/Libraries/nightSkyFireworks'
import Vapor from 'laravel-vapor'
import {
  computed,
  onBeforeUnmount,
  onMounted,
  useId,
  useTemplateRef,
} from 'vue'

const uid = useId()
/** SVG ids unique per instance. */
const id = (name) => `night-sky-${uid}-${name}`

/** The smaller picture on narrow screens. */
const picture = computed(() =>
  Vapor.asset(
    typeof window !== 'undefined' && window.innerWidth < 1200
      ? 'images/ds-login/munich-night-1200.webp'
      : 'images/ds-login/munich-night.webp',
  ),
)

/**
 * A deterministic pseudo-random sequence (mulberry32), so the stars sit on the same spots on every
 * visit and in every test.
 */
const random = (() => {
  let seed = 20260926
  return () => {
    seed = (seed + 0x6d2b79f5) | 0
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
})()

/** The sky above the clouds, without the two planets in its upper corners. */
const inSky = (x, y) =>
  y < 430 && !(x < 260 && y < 170) && !(x > 1500 && y < 170)

/** Small stars, each with its own size, pace and start, so they never blink in step. */
const STARS = []
while (STARS.length < 48) {
  const x = Math.round(20 + random() * 1632)
  const y = Math.round(60 + random() * 370)
  if (!inSky(x, y)) {
    continue
  }
  STARS.push({
    x,
    y,
    r: Math.round((0.8 + random() * 0.9) * 10) / 10,
    style: {
      '--d': `${-Math.round(random() * 80) / 10}s`,
      '--t': `${Math.round((4 + random() * 5) * 10) / 10}s`,
    },
  })
}

/** A few bright stars that now and then glint (a four-pointed sparkle), mostly beside the card. */
const GLINTS = [
  [330, 118],
  [168, 262],
  [452, 330],
  [1262, 128],
  [1418, 238],
  [1580, 330],
  [1060, 210],
].map(([x, y], index) => ({
  x,
  y,
  style: {
    '--d': `${-index * 1.7}s`,
    '--t': `${6 + (index % 3) * 1.5}s`,
  },
}))

/** Twenty lamps round the rim. */
const BULBS = Array.from({ length: 20 }, (_, index) => index * 18)

/** Smoke from the tents' tops: start x/y, delay and duration. */
const PUFFS = [
  { x: 1262, y: 786, d: '0s', t: '24s' },
  { x: 1292, y: 790, d: '-9s', t: '28s' },
  { x: 1412, y: 762, d: '-16s', t: '26s' },
  { x: 1462, y: 768, d: '-5s', t: '30s' },
].map((puff) => ({
  ...puff,
  style: {
    '--sx': `${puff.x}px`,
    '--sy': `${puff.y}px`,
    '--d': puff.d,
    '--t': puff.t,
  },
}))

const fireworks = useTemplateRef('fireworks')
let stop = () => {}

onMounted(() => {
  stop = startFireworks(fireworks.value)
})

onBeforeUnmount(() => stop())
</script>
